import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Loader2, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { apiKeysApi, ApiKeyScope, CreatedHubApiKey } from '@/lib/api';

interface CreateApiKeyModalProps {
  onClose: () => void;
  /** Handed the created key so the page can show it once, above the list. */
  onCreated: (key: CreatedHubApiKey) => void;
}

/** Expiry choices. `null` days = never. */
const EXPIRY_CHOICES: Array<{ label: string; days: number | null }> = [
  { label: 'Never', days: null },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
  { label: '1 year', days: 365 },
];

function expiryIso(days: number | null): string | null {
  if (days === null) return null;
  const date = new Date();
  date.setDate(date.getDate() + days);
  return date.toISOString();
}

export function CreateApiKeyModal({ onClose, onCreated }: CreateApiKeyModalProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const [expiryDays, setExpiryDays] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The picker is built from the SERVER's catalogue, so a scope cannot exist
  // in this list and not in the guard.
  const { data: scopes, isLoading: scopesLoading } = useQuery({
    queryKey: ['apiKeyScopes'],
    queryFn: () => apiKeysApi.scopes(),
  });

  const grouped = useMemo(() => {
    const groups = new Map<string, ApiKeyScope[]>();
    for (const scope of scopes ?? []) {
      const list = groups.get(scope.resource) ?? [];
      list.push(scope);
      groups.set(scope.resource, list);
    }
    return Array.from(groups.entries());
  }, [scopes]);

  const createMutation = useMutation({
    mutationFn: () =>
      apiKeysApi.create({ name: name.trim(), scopes: selected, expiresAt: expiryIso(expiryDays) }),
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys'] });
      onCreated(created);
    },
    onError: (err: any) => setError(err?.message || 'Could not create the key'),
  });

  const toggle = (value: string) => {
    setSelected((current) =>
      current.includes(value) ? current.filter((s) => s !== value) : [...current, value],
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Give the key a name — it is how you will recognise it later.');
      return;
    }
    if (selected.length === 0) {
      setError('Pick at least one scope — a key with none can authenticate but do nothing.');
      return;
    }
    setError(null);
    createMutation.mutate();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-secondary/60 backdrop-blur-sm animate-fade-in"
        onClick={onClose}
      />

      <div className="relative w-full max-w-lg bg-white rounded-xl shadow-xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-start justify-between p-6 pb-4">
          <div>
            <h2 className="text-heading-3 text-secondary">New API key</h2>
            <p className="text-sm text-grey-500 mt-1">
              The key is shown once, when it is created. It is stored only as a hash and cannot be
              recovered.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 -m-2 text-grey-400 hover:text-secondary rounded transition-colors"
            aria-label="Close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 pb-6 space-y-5">
          <div>
            <label htmlFor="api-key-name" className="block text-sm font-medium text-secondary mb-1.5">
              Name
            </label>
            <input
              id="api-key-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="financials.soliddigital.com"
              autoFocus
              className="input w-full"
            />
            <p className="text-xs text-grey-500 mt-1.5">Name it after the app that will hold it.</p>
          </div>

          <div>
            <span className="block text-sm font-medium text-secondary mb-1.5">Permissions</span>
            <p className="text-xs text-grey-500 mb-3">
              A key can only do what it is granted here. Give it the least it needs — a read-only key
              cannot write even if it leaks.
            </p>

            {scopesLoading ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-5 h-5 text-grey-400 animate-spin" />
              </div>
            ) : (
              <div className="space-y-4">
                {grouped.map(([resource, list]) => (
                  <div key={resource}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-grey-400 mb-2">
                      {resource === 'all' ? 'Everything' : resource}
                    </p>
                    <div className="space-y-2">
                      {list.map((scope) => {
                        const checked = selected.includes(scope.value);
                        return (
                          <label
                            key={scope.value}
                            className={cn(
                              'flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors',
                              checked
                                ? 'border-accent bg-accent/5'
                                : 'border-grey-100 hover:bg-grey-50',
                            )}
                          >
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggle(scope.value)}
                              className="mt-0.5"
                            />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-secondary">
                                {scope.label}
                              </span>
                              <span className="block text-xs text-grey-500 mt-0.5">
                                {scope.description}
                              </span>
                              <code className="block text-xs text-grey-400 mt-1 font-mono">
                                {scope.value}
                              </code>
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div>
            <span className="block text-sm font-medium text-secondary mb-1.5">Expires</span>
            <div className="flex flex-wrap gap-2">
              {EXPIRY_CHOICES.map((choice) => (
                <button
                  key={choice.label}
                  type="button"
                  onClick={() => setExpiryDays(choice.days)}
                  className={cn(
                    'px-3 py-1.5 rounded-lg text-sm border transition-colors',
                    expiryDays === choice.days
                      ? 'border-accent bg-accent/10 text-accent font-medium'
                      : 'border-grey-100 text-grey-600 hover:bg-grey-50',
                  )}
                >
                  {choice.label}
                </button>
              ))}
            </div>
            <p className="text-xs text-grey-500 mt-1.5">
              An expired key stops working the moment it expires. You can revoke it at any time
              regardless.
            </p>
          </div>

          {error && (
            <div className="p-3 rounded-lg bg-error/10 text-error text-sm">{error}</div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" isLoading={createMutation.isPending}>
              <ShieldCheck className="w-4 h-4 mr-2" />
              Create key
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
